import React, {useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useEvent, useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {profile$} from 'models/auth';
import {
    $allowedActionsForInvoice,
    $doesInvoiceExist,
    $invoiceState,
    fetchStorageLocationFx,
    fetchTinLegalNameFx,
    GeneralInfoGate,
} from 'models/documents/invoices/common';

import {
    goNextPage,
    updateInfoValues,
    $infoValues,
    updateIRN,
    arrStores,
    updateInfoValuesCityAndZip,
} from 'models/documents/invoices/create';

import {generalInfoPageValues} from 'models/documents/invoices/create/defaultVariables';

import {InfoValues} from 'types/invoices';

import {ControlSection} from '../../ControlSection';
import {formData} from '../../FormsData/GeneralInfo';
import {UniController} from '../../universalController';

import {Root, Grid, Headline, TextContainer, TextLabel, Text, TextSub} from './styled';
import {createIsFieldRequired, createValidationContext, generalInfoSchema} from './validation';

export const InvoiceGeneralInfoTab = () => {
    const profile = useStore(profile$);
    useEffect(() => {
        const props = {};
        GeneralInfoGate.set(props);
        GeneralInfoGate.open(props);
        return () => {
            GeneralInfoGate.close(props);
        };
    }, []);
    const {invoiceId}: {invoiceId: string} = useParams();
    const {t} = useTranslation();
    const defValues = useStore($infoValues);
    const invoiceState = useStore($invoiceState);
    const doesInvoiceExist = useStore($doesInvoiceExist);
    const onUpdateIRN = useEvent(updateIRN);
    const customerCompanyNameOrTIN = useEvent(fetchTinLegalNameFx);
    const storageLocationName = useEvent(fetchStorageLocationFx);
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);

    useEffect(() => {
        if (!profile?.legalPerson || doesInvoiceExist) {
            return;
        }

        const {company_name, legal_address, city, postal_code, country, tin} = profile.legalPerson;
        setValue('irn', defValues.irn);
        setValue('companyNameAndTIN', company_name);
        setValue('companyTIN', tin);
        // setValue('supplierBusinessDescription', defValues.supplierBusinessDescription);
        setValue('supplierStreetName', legal_address);
        setValue('supplierCityName', city);
        setValue('supplierPostalZone', postal_code);
        setValue('supplierCountry', country);
        setValue('supplierPhoneNumber', profile.phone);
        setValue('supplierEmail', profile.email);

        const valueForState = {
            ...generalInfoPageValues,
            companyNameAndTIN: company_name ?? '',
            companyTIN: tin ?? '',

            supplierStreetName: legal_address ?? '',
            supplierCityName: city ?? '',
            supplierPostalZone: postal_code ?? '',
            supplierCountry: country ?? '',

            supplierEmail: profile.email ?? '',
            supplierPhoneNumber: profile.phone ?? '',
        };

        updateInfoValues(valueForState);
    }, [profile, doesInvoiceExist]);

    const formContext = useMemo(() => createValidationContext(), []);

    const {
        control,
        setValue,
        handleSubmit,
        trigger,
        formState: {errors},
    } = useForm<InfoValues>({
        defaultValues: {...defValues},
        resolver: yupResolver(generalInfoSchema),
        context: formContext,
    });

    const isFieldRequired = useMemo(() => createIsFieldRequired(formContext), [formContext]);

    useEffect(() => {
        if (doesInvoiceExist) {
            return;
        }

        if (invoiceState !== defValues.irn) {
            onUpdateIRN(invoiceId);
        }
    }, [invoiceState]);

    useEffect(() => {
        setValue('irn', defValues.irn);
        //     setValue('companyNameAndTIN', defValues.companyNameAndTIN);
        //     setValue('supplierBusinessDescription', defValues.supplierBusinessDescription);
        //     setValue('supplierAddress', defValues.supplierAddress);
        //     setValue('supplierPhoneNumber', defValues.supplierPhoneNumber);
        //     setValue('supplierEmail', defValues.supplierEmail);
    }, [defValues]);

    const onSubmit = (data: any) => {
        updateInfoValues({
            ...data,
            supplierStreetName: defValues.supplierStreetName,
            supplierCityName: defValues.supplierCityName,
            supplierPostalZone: defValues.supplierPostalZone,
            supplierCountry: defValues.supplierCountry,
        });
        goNextPage();
    };

    const GEN_INFO = 'pages.documents.invoices.create.generalInformation';
    const HEADLINES = {
        general: t(`${GEN_INFO}.headlines.generalInformation`),
        supplier: t(`${GEN_INFO}.headlines.supplier`),
        supplierBankDetails: t(`${GEN_INFO}.headlines.supplierBankDetails`),
        customer: t(`${GEN_INFO}.headlines.customer`),
        customerStorageLocation: t(`${GEN_INFO}.headlines.customerStorageLocation`),
        terms: t(`${GEN_INFO}.headlines.terms`),
        bicRequired: t(`${GEN_INFO}.headlines.bicRequired`),
        ibanRequired: t(`${GEN_INFO}.headlines.ibanRequired`),
    };

    const removeNotAvailable = (val: string | undefined) => {
        if (!val) {
            return '';
        }
        return val === 'NOT_AVAILABLE' ? '' : val;
    };

    const formDataList = formData(
        t,
        allowedActionsForInvoice.edit,
        {
            customerCompanyNameOrTIN,
            storageLocationName,
        },
        isFieldRequired,
    );

    return (
        <Root>
            <div>
                <Headline>{HEADLINES.general}</Headline>
                <Grid columns={'1fr 1fr 1fr'}>
                    {formDataList.general.map((item, index) => (
                        <UniController
                            key={`${index}_${item.name}}`}
                            item={item}
                            index={index}
                            errors={errors}
                            control={control}
                            setValue={setValue}
                            trigger={trigger}
                        />
                    ))}
                </Grid>
            </div>
            <div>
                <Headline>{HEADLINES.supplier}</Headline>
                <Grid columns={'1fr 1fr'}>
                    {formDataList.supplier.map((item, index) =>
                        Array.isArray(item) ? (
                            <TextContainer key={`supplier_${index}`}>
                                <TextLabel>{t(`${GEN_INFO}.inputs.${item[0].name}`)}</TextLabel>
                                {item.map((itm, itmIndex) => {
                                    if (itm.name === 'companyNameAndTIN') {
                                        return (
                                            <Controller
                                                key={itmIndex}
                                                control={control}
                                                name={itm.name}
                                                render={({value, name}) => <Text>{value}</Text>}
                                            />
                                        );
                                    }
                                    return (
                                        <Controller
                                            key={itmIndex}
                                            control={control}
                                            name={itm.name}
                                            render={({value, name}) => <TextSub>{value}</TextSub>}
                                        />
                                    );
                                })}
                            </TextContainer>
                        ) : (
                            <TextContainer key={`supplier_${index}`}>
                                <TextLabel>{t(`${GEN_INFO}.inputs.${item.name}`)}</TextLabel>
                                <Controller
                                    control={control}
                                    name={item.name}
                                    render={({value, name}) => <Text>{value}</Text>}
                                />
                            </TextContainer>
                        ),
                    )}
                </Grid>
            </div>
            <div>
                <Headline>{HEADLINES.supplierBankDetails}</Headline>
                <Grid columns={'1fr 1fr'}>
                    {formDataList.supplierBankDetails.map((item, index) => (
                        <UniController
                            key={`${index}_${item.name}}`}
                            item={item}
                            index={index}
                            errors={errors}
                            control={control}
                            setValue={setValue}
                            trigger={trigger}
                        />
                    ))}
                </Grid>
            </div>
            <div>
                <Headline>{HEADLINES.customer}</Headline>
                <Grid columns={'1fr 1fr'}>
                    {formDataList.customerAuto.map((item, index) => (
                        <UniController
                            key={`${index}_${item.name}}`}
                            item={item}
                            index={index}
                            errors={errors}
                            control={control}
                            trigger={trigger}
                            setValue={setValue}
                            cb={
                                item.type === 'select'
                                    ? (x) => {
                                          //   setValue('customerEmail', `${x}`);
                                          //   setValue('customerPhoneNumber', `${x}`);
                                          setValue(
                                              'customerAddress',
                                              `${removeNotAvailable(
                                                  x.postalCode,
                                              )} ${removeNotAvailable(
                                                  x.country,
                                              )} ${removeNotAvailable(x.city)} ${removeNotAvailable(
                                                  x.street,
                                              )}`,
                                          );
                                          setValue('storageLocationName', '');
                                          setValue('storageLocationAddress', '');
                                          allowedActionsForInvoice.edit &&
                                              updateInfoValuesCityAndZip({
                                                  customerCity: x.city ?? '',
                                                  customerZip: x.postalCode ?? '',
                                                  customerCountry: x.country ?? '',
                                                  customerStreet: x.street ?? '',
                                              });
                                      }
                                    : undefined
                            }
                        />
                    ))}
                </Grid>
            </div>
            <div>
                <Headline>{HEADLINES.customerStorageLocation}</Headline>
                <Grid columns={'1fr 1fr'}>
                    {formDataList.customerStorageLocation.map((item, index) => (
                        <UniController
                            key={`${index}_${item.name}}`}
                            item={item}
                            index={index}
                            errors={errors}
                            control={control}
                            trigger={trigger}
                            setValue={setValue}
                            cb={
                                item.type === 'select'
                                    ? (x) => {
                                          setValue('storageLocationAddress', `${x.value}`);
                                      }
                                    : undefined
                            }
                        />
                    ))}
                </Grid>
            </div>
            <ControlSection
                nextIsDisabled={!!Object.keys(errors).length}
                onSubmit={handleSubmit(onSubmit)}
            />
        </Root>
    );
};
